import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useUser } from '@/contexts/UserContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Bell, Mail, MessageCircle, TrendingUp } from 'lucide-react';

// Email-only notification preferences. Push notifications were removed
// (we don't have a service-worker push pipeline and don't plan one for
// the foreseeable future). Stored as JSONB in profiles.notification_preferences.
interface NotificationPreferences {
  email_deals: boolean;
  email_mentions: boolean;
  email_messages: boolean;
  email_marketing: boolean;
  frequency: 'instant' | 'daily' | 'weekly';
}

const DEFAULT_PREFS: NotificationPreferences = {
  email_deals: true,
  email_mentions: true,
  email_messages: true,
  email_marketing: false,
  frequency: 'instant',
};

export const NotificationSettings = () => {
  const { currentUser } = useUser();
  const { toast } = useToast();
  const [settings, setSettings] = useState<NotificationPreferences>(DEFAULT_PREFS);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadNotificationSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  const loadNotificationSettings = async () => {
    if (!currentUser?.id) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('notification_preferences')
        .eq('id', currentUser.id)
        .maybeSingle();

      if (error) throw error;

      const prefs = data?.notification_preferences as Partial<NotificationPreferences> | null | undefined;
      if (prefs && typeof prefs === 'object' && !Array.isArray(prefs)) {
        setSettings({ ...DEFAULT_PREFS, ...prefs });
      }
    } catch (error) {
      console.error('Error loading notification settings:', error);
    }
  };

  const updateSetting = useCallback(<K extends keyof NotificationPreferences>(
    key: K,
    value: NotificationPreferences[K],
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const saveSettings = async () => {
    if (!currentUser?.id) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ notification_preferences: settings })
        .eq('id', currentUser.id);

      if (error) throw error;

      toast({
        title: 'Saved',
        description: 'Your notification preferences have been updated.',
      });
    } catch (error) {
      console.error('Error saving notification settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save notification settings. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Notifications
          </CardTitle>
          <CardDescription>
            Choose what email notifications you want to receive at {currentUser?.email}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="email-deals" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Deal updates and opportunities
            </Label>
            <Switch
              id="email-deals"
              checked={settings.email_deals}
              onCheckedChange={(checked) => updateSetting('email_deals', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="email-mentions" className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Social mentions and alerts
            </Label>
            <Switch
              id="email-mentions"
              checked={settings.email_mentions}
              onCheckedChange={(checked) => updateSetting('email_mentions', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="email-messages" className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              Direct messages and communications
            </Label>
            <Switch
              id="email-messages"
              checked={settings.email_messages}
              onCheckedChange={(checked) => updateSetting('email_messages', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="email-marketing">
              Marketing and promotional emails
            </Label>
            <Switch
              id="email-marketing"
              checked={settings.email_marketing}
              onCheckedChange={(checked) => updateSetting('email_marketing', checked)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notification Frequency</CardTitle>
          <CardDescription>
            How often you want to receive email summaries (does not affect transactional emails like
            payment confirmations or password resets — those always send instantly).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={settings.frequency} onValueChange={(value) => updateSetting('frequency', value as NotificationPreferences['frequency'])}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select frequency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="instant">Instant</SelectItem>
              <SelectItem value="daily">Daily Summary</SelectItem>
              <SelectItem value="weekly">Weekly Summary</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Button onClick={saveSettings} disabled={isLoading} className="w-full">
        {isLoading ? 'Saving...' : 'Save Notification Settings'}
      </Button>
    </div>
  );
};
